import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { pickSupportedAudioMime, extForMime } from "@/lib/audio";
import {
  CaretLeft,
  Microphone,
  Stop,
  CheckCircle,
  Spinner,
  FloppyDisk,
  CaretDown,
  PaperPlaneTilt,
} from "@phosphor-icons/react";

const todayIso = () => new Date().toISOString().slice(0, 10);

export default function TermEvaluation() {
  const { id } = useParams();
  const [player, setPlayer] = useState(null);
  const [taxonomy, setTaxonomy] = useState(null);
  const [evalDate, setEvalDate] = useState(todayIso());
  const [evals, setEvals] = useState({}); // category_id -> { rating, comment, status }
  const [saving, setSaving] = useState({});
  const [savedAt, setSavedAt] = useState({});
  const [openGroup, setOpenGroup] = useState(null);
  const [err, setErr] = useState("");
  const [publishing, setPublishing] = useState(false);
  const [publishResult, setPublishResult] = useState(null);

  // Voice
  const [recordingFor, setRecordingFor] = useState(null);
  const [transcribingFor, setTranscribingFor] = useState(null);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  useEffect(() => {
    api.get(`/players/${id}`).then((r) => setPlayer(r.data));
    api.get("/taxonomy").then((r) => {
      setTaxonomy(r.data);
      if (r.data.groups?.length) setOpenGroup(r.data.groups[0].id);
    });
  }, [id]);

  useEffect(() => {
    setPublishResult(null);
    api
      .get(`/term-evaluations`, { params: { player_id: id, evaluation_date: evalDate } })
      .then((r) => {
        const map = {};
        r.data.forEach((e) => {
          map[e.category_id] = { rating: e.rating, comment: e.comment, status: e.status };
        });
        setEvals(map);
      });
  }, [id, evalDate]);

  const setVal = (cid, patch) => setEvals((s) => ({ ...s, [cid]: { ...(s[cid] || {}), ...patch } }));

  const draftCount = Object.values(evals).filter((v) => v.status === "draft").length;

  const saveOne = async (cid) => {
    setSaving((s) => ({ ...s, [cid]: true }));
    setErr("");
    try {
      const res = await api.post("/term-evaluations", {
        player_id: id,
        evaluation_date: evalDate,
        category_id: cid,
        rating: evals[cid]?.rating || null,
        comment: evals[cid]?.comment || "",
      });
      setVal(cid, { status: res.data.status });
      setSavedAt((s) => ({ ...s, [cid]: Date.now() }));
      setTimeout(() => setSavedAt((s) => ({ ...s, [cid]: 0 })), 1800);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving((s) => ({ ...s, [cid]: false }));
    }
  };

  const publish = async () => {
    setPublishing(true);
    setErr("");
    try {
      const res = await api.post("/term-evaluations/publish", {
        player_id: id,
        evaluation_date: evalDate,
        base_url: window.location.origin,
      });
      setEvals((s) =>
        Object.fromEntries(
          Object.entries(s).map(([k, v]) => [k, { ...v, status: v.status === "draft" ? "published" : v.status }])
        )
      );
      setPublishResult(res.data);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setPublishing(false);
    }
  };

  const startRec = async (cid) => {
    setErr("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = pickSupportedAudioMime();
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        // Use the recorder's actual mime — iOS Safari may have chosen mp4 not webm
        const actualMime = rec.mimeType || mime || "audio/webm";
        const blob = new Blob(chunksRef.current, { type: actualMime });
        if (blob.size === 0) {
          setErr("No audio captured — check your microphone permission and try again.");
          return;
        }
        const ext = extForMime(actualMime);
        setTranscribingFor(cid);
        try {
          const fd = new FormData();
          fd.append("audio", blob, `voice.${ext}`);
          // Do NOT manually set Content-Type — axios needs to generate the multipart boundary.
          const res = await api.post("/transcribe", fd);
          const txt = (res.data?.text || "").trim();
          if (!txt) {
            setErr("Couldn't hear anything — try speaking a little louder or closer to the mic.");
          } else {
            setVal(cid, { comment: ((evals[cid]?.comment || "") + " " + txt).trim() });
          }
        } catch (e) {
          const reason = formatApiError(e.response?.data?.detail) || e.message;
          setErr(`Transcription failed: ${reason}`);
        } finally {
          setTranscribingFor(null);
        }
      };
      rec.start();
      mediaRef.current = rec;
      setRecordingFor(cid);
    } catch (e) {
      setErr("Microphone permission denied. Tap the lock icon in the address bar to allow it.");
    }
  };
  const stopRec = () => {
    if (mediaRef.current?.state !== "inactive") mediaRef.current.stop();
    setRecordingFor(null);
  };

  if (!player || !taxonomy)
    return <div className="font-label">Loading…</div>;

  return (
    <div className="space-y-5 pb-10" data-testid="term-evaluation-page">
      <Link to={`/players/${id}`} className="btn-ghost inline-flex items-center gap-1.5 -ml-2" data-testid="back-link">
        <CaretLeft size={18} weight="bold" /> Back
      </Link>

      <div className="surface p-5">
        <div className="font-label">Evaluation</div>
        <h1 className="font-heading uppercase text-3xl tracking-tight mt-1">{player.name}</h1>
        <div className="flex items-end gap-3 mt-3 flex-wrap">
          <div>
            <label className="font-label block mb-1">Session date</label>
            <input
              type="date"
              className="input"
              style={{ width: "auto" }}
              value={evalDate}
              max={todayIso()}
              onChange={(e) => e.target.value && setEvalDate(e.target.value)}
              data-testid="eval-date-input"
            />
          </div>
          <span className="text-xs text-zinc-500 pb-2.5">
            Evaluate on any date. Saved ratings stay as drafts until you publish.
          </span>
        </div>
      </div>

      {draftCount > 0 && (
        <div className="surface p-4 border border-[#e3735e]/40 flex items-center justify-between gap-3 flex-wrap" data-testid="publish-banner">
          <div className="min-w-0">
            <div className="font-semibold text-sm text-[#e3735e]">
              {draftCount} draft evaluation{draftCount !== 1 ? "s" : ""} — not visible to parents yet
            </div>
            <div className="text-xs text-zinc-500 mt-0.5">Publish when you&apos;re done to notify parents (in-app + email).</div>
          </div>
          <button onClick={publish} disabled={publishing} className="btn-primary shrink-0" data-testid="publish-evals-btn">
            <PaperPlaneTilt size={14} weight="bold" /> {publishing ? "Publishing…" : "Publish & notify"}
          </button>
        </div>
      )}

      {publishResult && (
        <div className="surface p-4 border border-[#16ec06]/40 text-sm text-[#16ec06]" data-testid="publish-success">
          Published {publishResult.published} evaluation{publishResult.published !== 1 ? "s" : ""}.{" "}
          {publishResult.parents_notified} parent{publishResult.parents_notified !== 1 ? "s" : ""} notified
          {publishResult.emails_sent ? ` (${publishResult.emails_sent} email${publishResult.emails_sent !== 1 ? "s" : ""} sent)` : ""}.
        </div>
      )}

      {err && <div className="text-sm text-[#ff0026] bg-[#ff0026]/10 border border-[#ff0026]/30 rounded-md p-3" data-testid="eval-error">{err}</div>}

      {taxonomy.groups.map((g) => {
        const cats = taxonomy.categories.filter((c) => c.category_group_id === g.id);
        const completed = cats.filter((c) => evals[c.id]?.rating).length;
        const open = openGroup === g.id;
        return (
          <div key={g.id} className="surface overflow-hidden" data-testid={`group-${g.id}`}>
            <button
              className="w-full px-4 py-3.5 flex items-center justify-between text-left hover:bg-[#1a1a1a]"
              onClick={() => setOpenGroup(open ? null : g.id)}
              data-testid={`group-toggle-${g.id}`}
            >
              <div>
                <div className="font-heading uppercase text-lg tracking-tight">{g.name}</div>
                <div className="font-label mt-0.5">{completed} / {cats.length} rated</div>
              </div>
              <CaretDown size={20} weight="bold" className={`transition-transform ${open ? "rotate-180" : ""} text-zinc-500`} />
            </button>
            {open && (
              <div className="border-t border-[#27272a]">
                {cats.map((c) => {
                  const val = evals[c.id] || {};
                  return (
                    <div key={c.id} className="px-4 py-4 border-b border-[#27272a] last:border-b-0" data-testid={`category-row-${c.id}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-semibold text-sm">{c.name}</span>
                        {val.status === "draft" && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-[#e3735e]/15 text-[#e3735e] font-bold rounded uppercase tracking-wider" data-testid={`status-draft-${c.id}`}>
                            Draft
                          </span>
                        )}
                        {val.status === "published" && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-[#16ec06]/15 text-[#16ec06] font-bold rounded uppercase tracking-wider" data-testid={`status-published-${c.id}`}>
                            Published
                          </span>
                        )}
                      </div>
                      {/* Rating */}
                      <div className="flex gap-1.5 mb-2.5" data-testid={`rating-${c.id}`}>
                        {[1, 2, 3, 4, 5].map((n) => (
                          <button
                            key={n}
                            onClick={() => setVal(c.id, { rating: val.rating === n ? null : n })}
                            className={`flex-1 h-11 rounded-md font-heading text-xl font-bold transition-colors ${
                              val.rating === n
                                ? "bg-[#dfff00] text-black"
                                : "bg-[#1a1a1a] border border-[#27272a] text-zinc-400 hover:text-white"
                            }`}
                            data-testid={`rating-${c.id}-${n}`}
                          >
                            {n}
                          </button>
                        ))}
                      </div>
                      {/* Comment + mic */}
                      <div className="flex gap-2 items-start">
                        <button
                          type="button"
                          onClick={() => (recordingFor === c.id ? stopRec() : startRec(c.id))}
                          disabled={transcribingFor === c.id || (recordingFor && recordingFor !== c.id)}
                          className={`shrink-0 w-12 h-12 rounded-md flex items-center justify-center ${
                            recordingFor === c.id
                              ? "bg-[#dfff00] text-black mic-recording"
                              : "bg-[#1a1a1a] border border-[#27272a] text-[#dfff00] hover:border-[#dfff00]/50"
                          }`}
                          data-testid={`mic-${c.id}`}
                          aria-label="Voice note"
                        >
                          {transcribingFor === c.id ? <Spinner size={20} className="animate-spin" /> : recordingFor === c.id ? <Stop size={20} weight="fill" /> : <Microphone size={20} weight="bold" />}
                        </button>
                        <textarea
                          className="input"
                          rows={2}
                          placeholder="Coach comment for this category…"
                          value={val.comment || ""}
                          onChange={(e) => setVal(c.id, { comment: e.target.value })}
                          data-testid={`comment-${c.id}`}
                        />
                      </div>
                      <div className="flex justify-end mt-2">
                        <button
                          onClick={() => saveOne(c.id)}
                          disabled={saving[c.id]}
                          className="px-4 h-10 bg-[#dfff00] text-black rounded-md font-bold text-xs uppercase tracking-wider flex items-center gap-1.5 disabled:opacity-50 hover:bg-[#e5ff33]"
                          data-testid={`save-${c.id}`}
                        >
                          {saving[c.id] ? "Saving…" : savedAt[c.id] ? (<><CheckCircle size={14} weight="fill" /> Saved</>) : (<><FloppyDisk size={14} weight="bold" /> Save</>)}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
